// src/pages/testimonials/controllers/Testimonials.controller.tsx
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TestimonialsViewProps, Testimonial, SubmitTestimonialPayload, SubmitTestimonialResult } from "../types";
import { useTestimonials } from "../../../hooks/useTestimonials";
import { useDocumentTitle } from "../../../hooks/useDocumentTitle";

type ApiLanguage = "pt" | "en" | "es";

const toApiLanguage = (raw?: string): ApiLanguage => {
  const lang = (raw || "pt").slice(0, 2).toLowerCase();
  return lang === "en" || lang === "es" || lang === "pt" ? (lang as ApiLanguage) : "pt";
};

export function TestimonialsController() {
  useDocumentTitle(
    "Depoimentos",
    "Depoimentos de pessoas que trabalharam com Cassio Silva Takarada."
  );
  const { i18n } = useTranslation();
  // Re-read on every render — React + useTranslation re-renders when i18n.language changes.
  const currentLang = toApiLanguage(i18n.language);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const {
    testimonials: testimonialsData,
    loading: testimonialsLoading,
    fetchTestimonials,
    createTestimonial,
    refresh,
  } = useTestimonials({ limit: 100 }, true, currentLang);

  // Defensive: directly subscribe to i18next's languageChanged event and
  // re-fetch. The hook's useCallback already depends on language, so in
  // theory the effect inside the hook would re-fire — but in practice some
  // edge cases (StrictMode, batching, namespace loading order) can leave a
  // stale closure that never sees the new language. This guarantees a
  // re-fetch with the freshest possible target.
  useEffect(() => {
    const onLangChange = (lng: string) => {
      const next = toApiLanguage(lng);
      fetchTestimonials({ page: 1, limit: 100, target: next } as any);
    };
    i18n.on("languageChanged", onLangChange);
    return () => {
      i18n.off("languageChanged", onLangChange);
    };
  }, [i18n, fetchTestimonials]);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  const testimonialsFallback: Testimonial[] = [
    {
      id: 1,
      name: "Mariana Souza",
      message:
        "Trabalhar com o Cassio foi uma experiência incrível. Sempre atencioso aos detalhes e focado em entregar valor real ao usuário.",
    },
    {
      id: 2,
      name: "Pedro Almeida",
      message:
        "Profissional muito competente, comunicação clara e excelente domínio técnico. O projeto evoluiu rapidamente com alta qualidade.",
    },
    {
      id: 3,
      name: "Ana Ribeiro",
      message:
        "Além de técnico, traz uma visão de produto muito madura. Recomendo fortemente para projetos complexos.",
    },
  ];

  // D1/Workers usa `id` (number) — antes (Mongo) era `_id` (string).
  const testimonials =
    testimonialsData.length > 0
      ? testimonialsData.map((test: any) => ({
          id: test.id ?? test._id,
          name: test.author,
          message: test.message,
        }))
      : testimonialsFallback;

  const onSubmitTestimonial = async (
    payload: SubmitTestimonialPayload
  ): Promise<SubmitTestimonialResult> => {
    const created = await createTestimonial(payload);
    if (!created) return "error";
    // Re-fetch so the new testimonial (already translated server-side) shows up.
    await refresh();
    return "ok";
  };

  const viewProps: TestimonialsViewProps = {
    isLoading: isLoading || testimonialsLoading,
    testimonials,
    onSubmitTestimonial,
  };

  return viewProps;
}

export default TestimonialsController;
